/**
 * <facet-filters> — the collection page's filters, sort and machine context.
 *
 * The facets and the sort are plain GET forms, so the page works without
 * this. With it: a change applies itself, the URL is kept in step (so Back,
 * Forward, refresh and a shared link all reproduce the same results), and
 * the section is re-rendered through the Section Rendering API rather than
 * reloading the page. Pagination stays as links — a new page is a new URL.
 *
 * Machine context: on a vehicle collection, says whether this is the garage's
 * active machine and offers to make it so; on a category collection with a
 * machine chosen, offers the machine's own collection narrowed to this
 * category by product type — collection membership, so a verified claim.
 */
(function () {
  'use strict';

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.setAttribute('data-live', '');

      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-facets-form]')) this.apply();
        else if (event.target.closest('[data-sort-select]')) this.apply();
      });
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-facets-form], [data-sort-form]')) {
          event.preventDefault();
          this.apply();
        }
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-facets-toggle]')) this.toggleDrawer(true);
        else if (event.target.closest('[data-facets-close]')) this.toggleDrawer(false);
        else if (event.target.closest('[data-machine-context-action]')) this.adopt();
        else {
          // Active-filter chips, the subcategory chips and the page links are
          // ordinary links; handle them in place so the URL and history stay
          // consistent.
          const chip = event.target.closest('[data-active-filters] a, .collection__subcats a, [data-facet-link], .pagination a');
          if (chip && chip.href) {
            event.preventDefault();
            this.navigate(chip.href);
          }
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.querySelector('[data-facets-panel][data-open]')) this.toggleDrawer(false);
      });

      window.addEventListener('popstate', () => this.render(window.location.href, { push: false }));
      document.addEventListener('garage:changed', () => this.paintMachine());
      this.paintMachine();
      // The feed and the card builder are deferred scripts that load before
      // this one (layout/theme.liquid). This element upgrades the moment its
      // own script runs, so should the order ever slip, what needs them waits
      // for the rest of the deferred scripts rather than silently doing nothing.
      const helpersReady = () => !!(window.CollectionFeed && window.SearchPage);
      const paintFromFeed = () => {
        this.paintStock();
        this.paintFeedResults(window.location.href);
      };
      if (helpersReady()) paintFromFeed();
      else document.addEventListener('DOMContentLoaded', paintFromFeed, { once: true });
      this.enforceStock();
    }

    /**
     * Out of stock is hidden server-side: when the page says the Availability
     * filter exists and the URL does not carry it, re-render with it, in
     * place (no history entry), so totals, pages and facets agree.
     */
    enforceStock() {
      if (this.dataset.enforceStock !== 'true') return;
      const url = new URL(window.location.href);
      if (url.searchParams.has('filter.v.availability')) return;
      url.searchParams.set('filter.v.availability', '1');
      this.render(url.toString(), { push: false, replace: true });
    }

    /** Build the URL for the current form state and go there. */
    apply() {
      // Start from what the URL carries that isn't a filter, sort or page —
      // on the search page that is the query and its type — then add the
      // form's filters and the sort.
      const params = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (!key.startsWith('filter.') && !['sort_by', 'page', 'sections'].includes(key)) params.append(key, value);
      }
      const form = this.querySelector('[data-facets-form]');
      if (form) {
        for (const [key, value] of new FormData(form).entries()) {
          if (value !== '' && key.startsWith('filter.')) params.append(key, value);
        }
      } else {
        for (const [key, value] of new URLSearchParams(window.location.search)) {
          if (key.startsWith('filter.')) params.append(key, value);
        }
      }
      const sort = this.querySelector('[data-sort-select]');
      if (sort && sort.value) params.set('sort_by', sort.value);
      const query = params.toString();
      this.navigate(`${this.dataset.url}${query ? `?${query}` : ''}`);
      this.toggleDrawer(false);
    }

    navigate(url) {
      this.render(url, { push: true });
    }

    async render(url, { push, replace = false }) {
      const id = this.dataset.sectionId;
      const target = new URL(url, window.location.origin);
      const token = (this.token = (this.token || 0) + 1);
      this.setAttribute('aria-busy', 'true');
      try {
        const sectionUrl = new URL(target);
        sectionUrl.searchParams.set('sections', id);
        // No Accept: application/json — Shopify would answer with the
        // collection's JSON and ignore ?sections=.
        const res = await fetch(sectionUrl.toString());
        if (!res.ok) throw new Error(`collection ${res.status}`);
        const html = (await res.json())[id];
        if (token !== this.token) return;
        if (!html) throw new Error('section missing');
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-collection]');
        if (!next) throw new Error('markup missing');
        if (push) history.pushState({}, '', target.pathname + target.search);
        else if (replace) history.replaceState({}, '', target.pathname + target.search);
        this.replaceChildren(...next.childNodes);
        this.paintMachine();
        this.paintStock();
        this.paintFeedResults(target);
        const count = this.querySelector('[data-collection-count]');
        if (count) count.setAttribute('aria-live', 'polite');
        window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - 16, behavior: 'auto' });
      } catch (err) {
        if (token !== this.token) return;
        // The URL still describes the results: fall back to a real navigation.
        console.error('[collection]', err);
        window.location.href = target.toString();
      } finally {
        if (token === this.token) this.removeAttribute('aria-busy');
      }
    }

    toggleDrawer(open) {
      const panel = this.querySelector('[data-facets-panel]');
      const toggle = this.querySelector('[data-facets-toggle]');
      if (!panel) return;
      panel.toggleAttribute('data-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        (panel.querySelector('[data-facets-close]') || panel).focus();
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        if (toggle && document.activeElement && panel.contains(document.activeElement)) toggle.focus();
      }
    }

    /* ---- Machine context ---- */

    paintMachine() {
      const active = window.Garage && window.Garage.active();
      const vehicleKey = this.dataset.vehicleKey;

      const context = this.querySelector('[data-machine-context]');
      if (context && vehicleKey) {
        const label = context.querySelector('[data-machine-context-label]');
        const action = context.querySelector('[data-machine-context-action]');
        const mine = active && active.key === vehicleKey;
        const saved = (window.Garage && window.Garage.vehicles || []).some((v) => v.key === vehicleKey);
        // The chosen machine's own page says nothing — the title already
        // names it (Lucas, 2026-10-12); another machine's page offers it:
        // added to the garage and made current in one go, worded for
        // whether it is already saved.
        context.hidden = !!mine;
        if (label) label.textContent = 'Every part here fits this machine';
        if (action) {
          action.hidden = !!mine;
          action.textContent = saved ? 'Make it my machine' : 'Add it to my garage';
        }
      }

      const offer = this.querySelector('[data-fit-offer]');
      if (offer && !vehicleKey) {
        if (active) this.paintOffer(offer, active);
        else offer.hidden = true;
      }
    }

    /**
     * The offer on a category page: this category narrowed to the garage's
     * machine. The machine's feed says whether the category is one of its
     * lvl2 categories or lvl3 product types, and how many are in stock; with
     * none, the link goes to the machine's whole collection instead of a
     * filtered page with nothing on it (Lucas, 2026-10-12). Until the feed
     * answers, the plain collection is the safe link.
     */
    paintOffer(offer, active) {
      const link = offer.querySelector('[data-fit-offer-link]');
      const name = offer.querySelector('[data-fit-offer-machine]');
      const text = offer.querySelector('[data-fit-offer-text]');
      const base = `/collections/${encodeURIComponent(active.key)}`;
      const title = this.dataset.categoryTitle || '';
      if (name) name.textContent = active.nickname || active.label;
      if (link) link.href = base;
      if (text) text.textContent = 'See all parts for my';
      offer.hidden = false;
      offer.dataset.fitOfferState = 'all';
      if (!window.CollectionFeed || !title) return;
      const token = (this.offerToken = (this.offerToken || 0) + 1);
      window.CollectionFeed.load(active.key).then((products) => {
        if (token !== this.offerToken || !this.isConnected) return;
        const inStock = products.filter((p) => (p.variants || []).some((v) => v.available));
        const count = (tag) => inStock.filter((p) => (p.tags || []).includes(tag)).length;
        const asCategory = count(`ZX2_${title}`);
        const asType = count(`ZX3_${title}`);
        const param = asCategory ? 'filter.p.m.fixmytoys.lvl2' : asType ? 'filter.p.m.fixmytoys.lvl3' : null;
        const n = asCategory || asType;
        const tag = asCategory ? `ZX2_${title}` : `ZX3_${title}`;
        this.leadWithFits(param ? inStock.filter((p) => (p.tags || []).includes(tag)) : []);
        if (!param) return; // nothing here fits: the whole collection stands
        if (link) link.href = `${base}?${param}=${encodeURIComponent(title)}`;
        if (text) text.textContent = `Only show the ${n} ${n === 1 ? 'part' : 'parts'} here that fit my`;
        offer.dataset.fitOfferState = 'narrow';
      }).catch(() => {});
    }

    /**
     * Every part in this category that fits the machine leads the first
     * page — not only the ones Shopify happened to put on it (Lucas, #119:
     * "10 fit, 4 on page 1, 1 on page 2, 2 on page 5"). The fitting parts
     * the page lacks are built from the machine's feed and put in front;
     * fits-badges.js then marks them and keeps every fit first. Only on the
     * plain first page: a filtered or later page is left as Shopify sent it,
     * and a fit that is also on a later page stays there, marked.
     */
    leadWithFits(products) {
      const grid = this.querySelector('[data-collection-results] .product-grid');
      if (!grid || !window.SearchPage) return;
      for (const li of grid.querySelectorAll('li[data-fit-lead]')) li.remove();
      const params = new URLSearchParams(window.location.search);
      const page = Number(params.get('page')) || 1;
      // The enforced stock filter (enforceStock) is not a narrowing: only
      // in-stock fits are added anyway.
      if (page !== 1 || [...params.keys()].some((k) => k.startsWith('filter.') && k !== 'filter.v.availability')) return;
      const present = new Set([...grid.querySelectorAll('.product-card[data-product-handle]')].map((c) => c.dataset.productHandle));
      const missing = products.filter((p) => p.handle && !present.has(p.handle));
      if (!missing.length) return;
      const frag = document.createDocumentFragment();
      for (const product of missing) {
        const li = window.SearchPage.card(product); // an <li> holding the card
        li.setAttribute('data-fit-lead', '');
        frag.append(li);
      }
      grid.prepend(frag);
    }

    /**
     * A machine's page counts what a customer can buy. Shopify's count is a
     * total; the machine's feed knows availability, so with the setting on
     * the unfiltered total becomes "N in stock".
     */
    paintStock() {
      const key = this.dataset.vehicleKey;
      if (!key || this.dataset.countsInStock !== 'true' || !window.CollectionFeed) return;
      // The plain landing only: with the Availability filter enforced Shopify's
      // count is already in stock, and a filtered view's count is the feed
      // render's (paintFeedResults) or Shopify's.
      if (this.dataset.enforceStock === 'true') return;
      if ([...new URLSearchParams(window.location.search).keys()].some((k) => k.startsWith('filter.'))) return;
      const count = this.querySelector('[data-collection-count]');
      if (!count || !/^\d[\d,]*\s+products$/.test(count.textContent.trim())) return;
      window.CollectionFeed.load(key).then((products) => {
        if (!this.isConnected) return;
        const stock = window.CollectionFeed.stock(products);
        count.textContent = `${stock.inStock} in stock`;
        count.dataset.inStock = String(stock.inStock);
      }).catch(() => {});
    }

    /**
     * A machine's page, honest without the Availability filter.
     *
     * Out-of-stock products are hidden, but until the Availability filter is
     * on in Search & Discovery, Shopify cannot be asked for in-stock only:
     * its facet counts count everything, and a page can come up empty once
     * the hidden products are skipped (Lucas, 2026-10-10: "it is still
     * showing stuff that isn't there"). A machine's page has its feed — every
     * product, with availability and category tags — so the grid, the count
     * and the facet counts are rebuilt from it: in-stock products matching
     * the URL's filters, sorted and paged; the tree recounted, with empty
     * categories and types dropped. Once the filter is on
     * (data-enforce-stock="true") Shopify does all of this and this stays out.
     */
    paintFeedResults(url) {
      if (!this.dataset.vehicleKey || this.dataset.countsInStock !== 'true' || this.dataset.enforceStock === 'true') return;
      if (!window.CollectionFeed || !window.SearchPage) return;
      const token = this.token;
      window.CollectionFeed.load(this.dataset.vehicleKey).then((products) => {
        if (!this.isConnected || token !== this.token) return;
        this.renderFromFeed(products, new URL(url, window.location.origin));
      }).catch(() => {});
    }

    renderFromFeed(products, url) {
      const params = url.searchParams;
      const tagged = (p, prefix) => (p.tags || []).filter((t) => t.startsWith(prefix)).map((t) => t.slice(prefix.length));
      const inStock = products.filter((p) => (p.variants || []).some((v) => v.available));

      const category = params.get('filter.p.m.fixmytoys.lvl2');
      const types = params.getAll('filter.p.m.fixmytoys.lvl3');
      const vendors = params.getAll('filter.p.vendor');
      const min = Number(params.get('filter.v.price.gte')) || 0;
      const max = params.get('filter.v.price.lte') ? Number(params.get('filter.v.price.lte')) : Infinity;
      const price = (p) => Math.min(...(p.variants || []).filter((v) => v.available).map((v) => Number(v.price) || 0));
      const inCategory = (p) => !category || tagged(p, 'ZX2_').includes(category);
      const inTypes = (p) => !types.length || tagged(p, 'ZX3_').some((t) => types.includes(t));
      const byVendor = (p) => !vendors.length || vendors.includes(p.vendor);
      const inPrice = (p) => { const n = price(p); return n >= min && n <= max; };
      const matching = inStock.filter((p) => inCategory(p) && inTypes(p) && byVendor(p) && inPrice(p));

      // Counts as Shopify would give them if it knew: each category as if
      // picked (within the other filters), each type within the open
      // category, each brand within the category and types.
      const tally = (list, keyOf) => {
        const out = new Map();
        for (const p of list) for (const k of keyOf(p)) out.set(k, (out.get(k) || 0) + 1);
        return out;
      };
      const categories = tally(inStock.filter((p) => byVendor(p) && inPrice(p)), (p) => tagged(p, 'ZX2_'));
      const typeCounts = tally(inStock.filter((p) => inCategory(p) && byVendor(p) && inPrice(p)), (p) => tagged(p, 'ZX3_'));
      const vendorCounts = tally(inStock.filter((p) => inCategory(p) && inTypes(p) && inPrice(p)), (p) => [p.vendor]);

      this.recountFacets(categories, typeCounts, vendorCounts);
      this.paintGrid(matching, params);
    }

    /** The tree and the brand list say what is in stock; a value with nothing is dropped unless it is applied. */
    recountFacets(categories, typeCounts, vendorCounts) {
      const setCount = (node, n) => {
        const count = node?.querySelector('.facets__value-count');
        if (count) count.textContent = String(n);
      };
      for (const branch of this.querySelectorAll('[data-facet-branch]')) {
        const n = categories.get(branch.dataset.facetBranch) || 0;
        setCount(branch.querySelector('[data-facet-link]'), n);
        branch.hidden = n === 0 && !branch.classList.contains('facets__branch--open');
      }
      const recountInputs = (selector, counts) => {
        for (const input of this.querySelectorAll(selector)) {
          const n = counts.get(input.value) || 0;
          setCount(input.closest('label'), n);
          const item = input.closest('li');
          if (item) item.hidden = n === 0 && !input.checked;
        }
      };
      recountInputs('[data-facet-leaves] input[type="checkbox"]', typeCounts);
      recountInputs('[data-facets-form] input[type="checkbox"][name="filter.p.vendor"]', vendorCounts);
    }

    /** The in-stock matches, in the chosen order, a page at a time. */
    paintGrid(matching, params) {
      const results = this.querySelector('[data-collection-results]');
      if (!results) return;
      const sort = params.get('sort_by') || this.querySelector('[data-sort-select]')?.value || 'best-selling';
      let list;
      if (sort === 'title-descending') list = window.SearchPage.sortProducts(matching, 'title-ascending').reverse();
      else list = window.SearchPage.sortProducts(matching, sort); // the feed's own order is best selling
      const perPage = Number(this.dataset.perPage) || 24;
      const pages = Math.max(1, Math.ceil(list.length / perPage));
      const page = Math.min(pages, Math.max(1, Number(params.get('page')) || 1));

      results.replaceChildren();
      if (!list.length) {
        const empty = document.createElement('div');
        empty.className = 'collection__empty';
        const note = document.createElement('p');
        note.className = 'display-sm';
        note.textContent = 'Nothing in stock matches those filters right now.';
        const back = document.createElement('a');
        back.className = 'button button--outline';
        back.href = this.dataset.url;
        back.textContent = 'See everything in stock';
        empty.append(note, back);
        results.append(empty);
      } else {
        const grid = document.createElement('ul');
        grid.className = 'product-grid';
        for (const product of list.slice((page - 1) * perPage, page * perPage)) grid.append(window.SearchPage.card(product));
        results.append(grid);
        if (pages > 1) results.append(this.pagination(page, pages, params, list.length));
      }
      results.setAttribute('data-feed-results', '');
      const count = this.querySelector('[data-collection-count]');
      if (count) {
        count.textContent = `${list.length} in stock`;
        count.dataset.inStock = String(list.length);
      }
    }

    /** Page links in the shape of the section's own: first, last, the neighbours, gaps between. */
    pagination(page, pages, params, total) {
      const href = (n) => {
        const query = new URLSearchParams(params);
        query.delete('sections');
        if (n > 1) query.set('page', String(n)); else query.delete('page');
        const q = query.toString();
        return `${this.dataset.url}${q ? `?${q}` : ''}`;
      };
      const link = (className, text, n, rel) => {
        const a = document.createElement('a');
        a.className = className;
        a.href = href(n);
        a.textContent = text;
        if (rel) a.rel = rel;
        return a;
      };
      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('aria-label', 'Pagination');
      if (page > 1) nav.append(link('pagination__link pagination__link--prev', 'Previous', page - 1, 'prev'));
      const list = document.createElement('ul');
      list.className = 'pagination__pages';
      const shown = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
      let last = 0;
      for (const n of [...shown].sort((a, b) => a - b)) {
        if (n - last > 1) {
          const gap = document.createElement('li');
          const span = document.createElement('span');
          span.className = 'pagination__page pagination__page--gap';
          span.textContent = '…';
          gap.append(span);
          list.append(gap);
        }
        const item = document.createElement('li');
        if (n === page) {
          const current = document.createElement('span');
          current.className = 'pagination__page pagination__page--current';
          current.setAttribute('aria-current', 'page');
          current.textContent = String(n);
          item.append(current);
        } else {
          item.append(link('pagination__page', String(n), n));
        }
        list.append(item);
        last = n;
      }
      nav.append(list);
      if (page < pages) nav.append(link('pagination__link pagination__link--next', 'Next', page + 1, 'next'));
      const summary = document.createElement('p');
      summary.className = 'pagination__summary';
      summary.textContent = `Page ${page} of ${pages} · ${total} in stock`;
      const frag = document.createDocumentFragment();
      frag.append(nav, summary);
      return frag;
    }

    /** "Make it my machine": add this vehicle collection's machine to the garage. */
    async adopt() {
      const key = this.dataset.vehicleKey;
      if (!key || !window.Garage || !window.FitmentData) return;
      try {
        await window.FitmentData.load();
        const vehicle = window.FitmentData.fromKey(key);
        if (!vehicle) return;
        window.Garage.add(vehicle);
        if (typeof window.Garage.setActive === 'function') window.Garage.setActive(key);
      } catch (err) {
        console.error('[collection] adopt', err);
      }
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
